import * as React from "react";
import { useWebSocket } from "@/lib/cjs-interop";

const REFRESH_COALESCE_MS = 200;

const GRID_REFRESH_EVENTS = new Set([
  "snapshot_prompt_refresh_completed",
  "snapshot_review_updated",
  "snapshot_review_run_completed",
]);

interface SnapshotGridRefreshEvent {
  event: string;
  snapshot_collection_id: string;
}

const isRefreshEvent = (value: unknown): value is SnapshotGridRefreshEvent => {
  if (!value || typeof value !== "object") return false;
  const event = value as Record<string, unknown>;
  return (
    typeof event.event === "string" &&
    GRID_REFRESH_EVENTS.has(event.event) &&
    typeof event.snapshot_collection_id === "string"
  );
};

export const useSnapshotCollectionUpdates = (snapshotCollectionId: string | undefined, refetch: () => void): void => {
  const refetchRef = React.useRef(refetch);
  const timeoutRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  React.useEffect(
    () => () => {
      if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    },
    [snapshotCollectionId],
  );

  const onMessage = React.useCallback(
    (message: MessageEvent) => {
      let value: unknown;
      try {
        value = JSON.parse(String(message.data));
      } catch {
        return;
      }
      if (!isRefreshEvent(value) || value.snapshot_collection_id !== snapshotCollectionId) return;
      if (timeoutRef.current !== null) return;
      timeoutRef.current = window.setTimeout(() => {
        timeoutRef.current = null;
        refetchRef.current();
      }, REFRESH_COALESCE_MS);
    },
    [snapshotCollectionId],
  );

  const url = snapshotCollectionId
    ? `/pf-mainweb/business-reports/snapshot-collections/${encodeURIComponent(snapshotCollectionId)}/updates/ws`
    : null;
  useWebSocket(url, {
    onMessage,
    share: true,
    skipAssert: true,
    shouldReconnect: event => event?.code !== 1000 && event?.code !== 1008,
  });
};
